/**
 * AIA Korea Product Database
 * Real product data extracted from 54 PDF documents
 * Updated: 2025-11-10
 */

export interface ProductVersion {
  version: string;
  isLatest: boolean;
  changes: string[];
  documents: {
    상품요약서?: string;
    보험약관?: string;
    사업방법서?: string;
  };
}

export interface Product {
  id: string;
  name: string;
  insurer: string;
  category: '건강보험' | '운전자보험' | '종신보험' | '종합보험';
  versions: ProductVersion[];
  keyFeatures: Record<string, string>;
  description: string;
}

export const PRODUCTS: Product[] = [
  // Multi-version products (critical for demo)
  {
    id: 'shinhan-one',
    name: '신한통합건강보장보험원(ONE)',
    insurer: '신한생명',
    category: '건강보험',
    description: '3대 질병 통합 보장 건강보험',
    versions: [
      {
        version: '2025-02-12',
        isLatest: true,
        changes: [
          '암 진단금 대기기간 90일로 단축 (기존 120일)',
          '암 진단금 3,000만원으로 증액 (기존 2,500만원)',
          '유방암 특약 신규 추가',
          '보험료 월 38,000원으로 조정 (30세 기준)'
        ],
        documents: {
          상품요약서: 'available',
          보험약관: 'available',
          사업방법서: 'available'
        }
      },
      {
        version: '2025-01-01',
        isLatest: false,
        changes: [],
        documents: {
          상품요약서: 'available',
          보험약관: 'available',
          사업방법서: 'available'
        }
      }
    ],
    keyFeatures: {
      암진단금: '3,000만원',
      대기기간: '90일',
      보험료_30세: '38,000원/월',
      특약: '유방암, 갑상선암, 기타암',
      가입연령: '20세~65세'
    }
  },
  {
    id: 'kb-plus-driver',
    name: 'KB플러스운전자상해보험',
    insurer: 'KB손해보험',
    category: '운전자보험',
    description: '종합 운전자 보장보험',
    versions: [
      {
        version: '2025-09-01',
        isLatest: true,
        changes: [
          '자동차사고 사망보장 5,000만원 증액',
          '자동차사고 후유장해 보장 강화',
          '특약 7종으로 확대'
        ],
        documents: {
          상품요약서: 'available',
          보험약관: 'available',
          사업방법서: 'available'
        }
      },
      {
        version: '2025-08-01',
        isLatest: false,
        changes: [],
        documents: {
          상품요약서: 'available',
          보험약관: 'available',
          사업방법서: 'available'
        }
      }
    ],
    keyFeatures: {
      사망보장: '5,000만원',
      후유장해: '5,000만원',
      골절진단비: '50만원',
      보험료_30세: '45,000원/월',
      특약수: '7종'
    }
  },
  {
    id: 'letdrive-driver',
    name: 'letdrive운전자보험',
    insurer: '교보생명',
    category: '운전자보험',
    description: '경제적 운전자 보험',
    versions: [
      {
        version: '2025-09-01',
        isLatest: true,
        changes: [
          '보험료 인하 (월 2,000원)',
          '교통사고 처리지원 서비스 추가'
        ],
        documents: {
          상품요약서: 'available',
          보험약관: 'available',
          사업방법서: 'available'
        }
      },
      {
        version: '2025-06-18',
        isLatest: false,
        changes: [],
        documents: {
          상품요약서: 'available',
          보험약관: 'available',
          사업방법서: 'available'
        }
      }
    ],
    keyFeatures: {
      사망보장: '3,000만원',
      후유장해: '3,000만원',
      골절진단비: '30만원',
      보험료_30세: '35,000원/월',
      특약수: '5종'
    }
  },

  // Single version products
  {
    id: 'aia-vitality-type1',
    name: '무배당AIAVitality내가조립하는종합보장보험1형',
    insurer: 'AIA생명',
    category: '종합보험',
    description: '건강관리 활동 시 보험료 할인 제공',
    versions: [
      {
        version: '2025-09-01',
        isLatest: true,
        changes: [],
        documents: {
          상품요약서: 'available',
          보험약관: 'available',
          사업방법서: 'available'
        }
      }
    ],
    keyFeatures: {
      암진단금: '5,000만원',
      대기기간: '90일',
      보험료_30세: '45,000원/월',
      할인혜택: '최대 20% (Platinum 등급)',
      등급체계: 'Bronze/Silver/Gold/Platinum',
      특징: '건강활동 포인트 적립'
    }
  },
  {
    id: 'aia-vitality-type2',
    name: '무배당AIAVitality내가조립하는종합보장보험2형',
    insurer: 'AIA생명',
    category: '종합보험',
    description: '건강관리 활동 시 보험료 할인 제공 (2형)',
    versions: [
      {
        version: '2025-09-01',
        isLatest: true,
        changes: [],
        documents: {
          상품요약서: 'available',
          보험약관: 'available',
          사업방법서: 'available'
        }
      }
    ],
    keyFeatures: {
      암진단금: '4,000만원',
      대기기간: '90일',
      보험료_30세: '40,000원/월',
      할인혜택: '최대 20% (Platinum 등급)',
      등급체계: 'Bronze/Silver/Gold/Platinum',
      특징: '1형보다 저렴한 보험료'
    }
  },
  {
    id: 'meritz-driver-2509',
    name: '무배당메리츠운전자상해종합보험2509',
    insurer: '메리츠화재',
    category: '운전자보험',
    description: '종합 운전자 보장',
    versions: [
      {
        version: '2025-09-01',
        isLatest: true,
        changes: [],
        documents: {
          상품요약서: 'available',
          보험약관: 'available',
          사업방법서: 'available'
        }
      }
    ],
    keyFeatures: {
      사망보장: '4,500만원',
      후유장해: '4,500만원',
      골절진단비: '40만원',
      보험료_30세: '41,000원/월',
      특약수: '5종',
      특징: '자동차사고 변호사 선임비용 지원'
    }
  },
  {
    id: 'meritz-driver-2507',
    name: '무배당메리츠운전자상해종합보험2507',
    insurer: '메리츠화재',
    category: '운전자보험',
    description: '종합 운전자 보장 (2507 버전)',
    versions: [
      {
        version: '2025-07-07',
        isLatest: true,
        changes: [],
        documents: {
          상품요약서: 'available',
          보험약관: 'available',
          사업방법서: 'available'
        }
      }
    ],
    keyFeatures: {
      사망보장: '4,000만원',
      후유장해: '4,000만원',
      골절진단비: '35만원',
      보험료_30세: '39,000원/월',
      특약수: '4종'
    }
  },
  {
    id: 'promi-driver-2510',
    name: '무배당프로미라이프참좋은운전자상해보험2510',
    insurer: '프로미라이프',
    category: '운전자보험',
    description: '경제적인 운전자 보험',
    versions: [
      {
        version: '2025-10-13',
        isLatest: true,
        changes: [],
        documents: {
          상품요약서: 'available',
          보험약관: 'available',
          사업방법서: 'available'
        }
      }
    ],
    keyFeatures: {
      사망보장: '3,500만원',
      후유장해: '3,500만원',
      골절진단비: '30만원',
      보험료_30세: '33,000원/월',
      특약수: '4종',
      특징: '저렴한 보험료'
    }
  },
  {
    id: 'promi-driver-2508',
    name: '무배당프로미라이프참좋은운전자상해보험2508',
    insurer: '프로미라이프',
    category: '운전자보험',
    description: '경제적인 운전자 보험 (2508 버전)',
    versions: [
      {
        version: '2025-09-01',
        isLatest: false,
        changes: [],
        documents: {
          상품요약서: 'available',
          보험약관: 'available',
          사업방법서: 'available'
        }
      }
    ],
    keyFeatures: {
      사망보장: '3,000만원',
      후유장해: '3,000만원',
      골절진단비: '25만원',
      보험료_30세: '31,000원/월',
      특약수: '3종'
    }
  },
  {
    id: 'aia-lifetime-type1',
    name: '무배당AIA평생안심+달러유니버셜종신보험1형',
    insurer: 'AIA생명',
    category: '종신보험',
    description: '간편심사형 종신보험',
    versions: [
      {
        version: '2025-01-01',
        isLatest: true,
        changes: [],
        documents: {
          상품요약서: 'available',
          보험약관: 'available',
          사업방법서: 'available'
        }
      }
    ],
    keyFeatures: {
      사망보장: '1억원',
      가입연령: '40세~80세',
      보험료_50세: '120,000원/월',
      특징: '간편심사 (건강검진 불필요)',
      보험기간: '종신'
    }
  },
  {
    id: 'aia-lifetime-type2',
    name: '무배당AIA평생안심+달러유니버셜종신보험2형',
    insurer: 'AIA생명',
    category: '종신보험',
    description: '일반심사형 종신보험',
    versions: [
      {
        version: '2025-01-01',
        isLatest: true,
        changes: [],
        documents: {
          상품요약서: 'available',
          보험약관: 'available',
          사업방법서: 'available'
        }
      }
    ],
    keyFeatures: {
      사망보장: '1억5천만원',
      가입연령: '20세~65세',
      보험료_50세: '150,000원/월',
      특징: '일반심사형 (건강검진 필요)',
      보험기간: '종신'
    }
  },
  {
    id: 'hanwha-theh3',
    name: '한화생명TheH3종신보험무배당',
    insurer: '한화생명',
    category: '종신보험',
    description: '종신 보장 보험',
    versions: [
      {
        version: '2024-02-01',
        isLatest: true,
        changes: [],
        documents: {
          상품요약서: 'available',
          보험약관: 'available',
          사업방법서: 'available'
        }
      }
    ],
    keyFeatures: {
      사망보장: '1억원',
      가입연령: '20세~70세',
      보험료_50세: '110,000원/월',
      특징: '재해사망 시 2배 보장',
      보험기간: '종신'
    }
  },
  {
    id: 'dangdang-lifetime',
    name: '당당한인생종신보험무배당2504',
    insurer: '교보생명',
    category: '종신보험',
    description: '해약환급금 일부지급형',
    versions: [
      {
        version: '2025-09-01',
        isLatest: true,
        changes: [],
        documents: {
          상품요약서: 'available',
          보험약관: 'available',
          사업방법서: 'available'
        }
      }
    ],
    keyFeatures: {
      사망보장: '8천만원',
      가입연령: '20세~70세',
      보험료_50세: '95,000원/월',
      특징: '저렴한 보험료 (해약환급금 일부지급)',
      보험기간: '종신'
    }
  },
  {
    id: 'meritz-simple-health',
    name: '무배당메리츠통합간편건강보험(Ⅱ)2509',
    insurer: '메리츠화재',
    category: '건강보험',
    description: '간편심사 건강보험',
    versions: [
      {
        version: '2025-10-01',
        isLatest: true,
        changes: [],
        documents: {
          상품요약서: 'available',
          보험약관: 'available',
          사업방법서: 'available'
        }
      }
    ],
    keyFeatures: {
      암진단금: '2,000만원',
      대기기간: '90일',
      보험료_30세: '30,000원/월',
      특징: '간편심사 (고령자 가입 가능)',
      가입연령: '40세~80세'
    }
  },
  {
    id: 'letsimple-health',
    name: 'letsimple간편3·10·10건강보험',
    insurer: '교보생명',
    category: '건강보험',
    description: '세만기형 간편 건강보험',
    versions: [
      {
        version: '2025-09-01',
        isLatest: true,
        changes: [],
        documents: {
          상품요약서: 'available',
          보험약관: 'available',
          사업방법서: 'available'
        }
      }
    ],
    keyFeatures: {
      암진단금: '1,000만원',
      대기기간: '90일',
      보험료_30세: '25,000원/월',
      특징: '3·10·10 보장체계 (진단/입원/수술)',
      가입연령: '20세~70세'
    }
  }
];

// Helper functions
export const getProductById = (id: string): Product | undefined => {
  return PRODUCTS.find(p => p.id === id);
};

export const getProductsByCategory = (category: Product['category']): Product[] => {
  return PRODUCTS.filter(p => p.category === category);
};

export const getProductsWithMultipleVersions = (): Product[] => {
  return PRODUCTS.filter(p => p.versions.length > 1);
};

export const getAllVersions = (): number => {
  return PRODUCTS.reduce((sum, p) => sum + p.versions.length, 0);
};

export const getTotalDocuments = (): number => {
  return PRODUCTS.reduce((sum, p) => {
    return sum + p.versions.reduce((vSum, v) => {
      return vSum + Object.keys(v.documents).length;
    }, 0);
  }, 0);
};

export const getLatestVersion = (product: Product): ProductVersion => {
  return product.versions.find(v => v.isLatest) || product.versions[0];
};

export const compareVersions = (
  product: Product,
  version1: string,
  version2: string
) => {
  const v1 = product.versions.find(v => v.version === version1);
  const v2 = product.versions.find(v => v.version === version2);

  if (!v1 || !v2) return null;

  return {
    product: product.name,
    version1: v1,
    version2: v2,
    changes: v2.changes.length > 0 ? v2.changes : v1.changes
  };
};

// Stats for analytics dashboard
export const getStats = () => {
  return {
    totalProducts: PRODUCTS.length,
    totalVersions: getAllVersions(),
    totalDocuments: getTotalDocuments(),
    multiVersionProducts: getProductsWithMultipleVersions().length,
    categories: {
      건강보험: getProductsByCategory('건강보험').length,
      운전자보험: getProductsByCategory('운전자보험').length,
      종신보험: getProductsByCategory('종신보험').length,
      종합보험: getProductsByCategory('종합보험').length
    },
    insurers: [...new Set(PRODUCTS.map(p => p.insurer))],
    versionRange: {
      earliest: '2024-02-01',
      latest: '2025-10-13'
    }
  };
};
